/**
 * Service pour logger les erreurs frontend vers le backend
 */

const API_ENDPOINT = '/api/frontend-error';

// Sauvegarder la référence originale avant tout override (doit être au scope module
// pour que processErrorQueue puisse l'utiliser sans déclencher la boucle)
const originalConsoleError = console.error;

// File d'attente pour les erreurs en cas de problème réseau
let errorQueue = [];
let isProcessing = false;

/**
 * Détecte les erreurs provenant d'extensions navigateur (LastPass, MetaMask, 1Password...)
 * Ces erreurs ne sont pas liées à l'application et ne doivent pas être loggées.
 */
const EXTENSION_MESSAGE_PATTERNS = [
  // LastPass / gestionnaires de mots de passe
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // Wallets crypto (MetaMask, Coinbase Wallet, Phantom...) injectés dans la page
  /Cannot read properties of undefined \(reading 'M_ID'\)/,
  /Cannot redefine property: (ethereum|solana|tronLink)/,
  /(MetaMask|ethereum provider|window\.ethereum|selectedAddress)/i,
  // Extensions qui coupent leur propre canal de messages
  /message (channel|port) closed before a response was received/i,
  /Extension context invalidated/i,
  /Receiving end does not exist/i,
  /The message port closed before a response/i,
  // Schémas d'URL propres aux extensions
  /(chrome|moz|safari|edge|chrome-untrusted)-extension:\/\//,
];

const isBrowserExtensionError = (message, stack = '') => {
  const msg = message == null ? '' : String(message);
  const stk = stack == null ? '' : String(stack);
  if (!msg && !stk) return false;

  // Une stack qui pointe vers un fichier d'extension n'est jamais du code applicatif.
  if (/(chrome|moz|safari|edge|chrome-untrusted)-extension:\/\//.test(stk)) {
    return true;
  }

  return EXTENSION_MESSAGE_PATTERNS.some((pattern) => pattern.test(msg));
};

/**
 * Envoie une erreur au backend pour logging
 * @param {Error} error - L'objet erreur
 * @param {Object} errorInfo - Informations additionnelles (ex: componentStack de React)
 * @param {string} url - URL où l'erreur s'est produite
 */
export const logErrorToBackend = async (error, errorInfo = {}, url = window.location.href) => {
  const msg = error?.message || error?.toString() || '';
  const rawStack = error?.stack || errorInfo?.stack || '';
  if (isBrowserExtensionError(msg, rawStack)) {
    return;
  }

  // Serialize error properly — Error properties are non-enumerable so JSON.stringify gives {}.
  // Extract message and stack explicitly, and fall back to JSON for plain objects.
  const errorMessage = error instanceof Error
    ? (error.message || error.name || 'Unknown error')
    : (typeof error === 'object' ? JSON.stringify(error) : String(error)) || 'Unknown error';
  const errorStack = error instanceof Error ? (error.stack || '') : '';

  const errorData = {
    message: errorMessage,
    stack: errorStack,
    url: url,
    componentStack: errorInfo.componentStack || '',
    errorInfo: JSON.stringify(errorInfo),
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    platform: navigator.platform,
    language: navigator.language
  };

  // Ajouter à la file d'attente
  errorQueue.push(errorData);
  
  // Traiter la file d'attente
  processErrorQueue();
};

/**
 * Traite la file d'attente d'erreurs
 */
const processErrorQueue = async () => {
  if (isProcessing || errorQueue.length === 0) {
    return;
  }

  isProcessing = true;

  while (errorQueue.length > 0) {
    const errorData = errorQueue[0];
    
    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });

      if (response.ok) {
        // Retirer de la file si succès
        errorQueue.shift();
      } else {
        // Arrêter si échec (retry plus tard)
        console.warn('Failed to log error, will retry later');
        break;
      }
    } catch (logError) {
      // Utiliser l'original pour éviter de déclencher la boucle d'erreurs
      originalConsoleError.call(console, 'Failed to send error to backend:', logError);
      break;
    }
  }

  isProcessing = false;
};

/**
 * Vérifie si le user agent correspond à un bot/crawler
 */
const isBot = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'bot', 'crawler', 'spider', 'scraper', 'slurp', 'mediapartners',
    'ahrefsbot', 'googlebot', 'bingbot', 'yandexbot', 'baiduspider',
    'facebookexternalhit', 'twitterbot', 'linkedinbot', 'pinterest',
    'slackbot', 'whatsapp', 'telegram', 'semrushbot', 'dotbot'
  ];
  return botPatterns.some(pattern => userAgent.includes(pattern));
};

/**
 * Handler global pour les erreurs non capturées
 */
export const setupGlobalErrorHandler = () => {
  // Ne pas logger les erreurs pour les bots/crawlers
  if (isBot()) {
    return;
  }

  // 1. Capturer les erreurs JavaScript non gérées
  window.addEventListener('error', (event) => {
    // Éviter de logger les erreurs de ressources (elles sont capturées ailleurs)
    if (event.target !== window) {
      // Utiliser currentSrc pour avoir l'URL réelle qui a échoué (srcset/webp/etc.)
      const src = event.target.currentSrc || event.target.src || event.target.href;
      const tagName = event.target.tagName;
      
      // Ignorer les erreurs de prefetch/preload (liens de navigation React Router)
      if (tagName === 'LINK' && event.target.rel && 
          (event.target.rel.includes('prefetch') || 
           event.target.rel.includes('preload') ||
           event.target.rel.includes('dns-prefetch') ||
           event.target.rel.includes('preconnect'))) {
        return; // Ne pas logger ces "erreurs"
      }
      
      // Ignorer les erreurs de chargement de pages React Router (navigation client-side)
      if (tagName === 'LINK' && src && !src.match(/\.(css|jpg|jpeg|png|gif|svg|webp|avif|woff|woff2|ttf|eot|ico)$/i)) {
        return; // Ce sont des liens de navigation, pas des ressources réelles
      }

      // Ignorer les erreurs d'images dans un élément <picture> :
      // le browser tente d'abord le WebP, s'il échoue il fire un error event mais
      // passe automatiquement au fallback PNG/src — OptimizedImage gère ce cas.
      if (tagName === 'IMG' && event.target.closest && event.target.closest('picture')) {
        return;
      }

      // Pour les images hors <picture>, vérifier que l'URL est réellement inaccessible
      // avant de logger. Évite les faux positifs causés par des erreurs réseau transitoires
      // au chargement initial (l'image est accessible mais la requête initiale échoue).
      if (tagName === 'IMG' && src) {
        const verifySrc = src;
        const verifyImg = new Image();
        verifyImg.onload = () => {}; // Image accessible → faux positif, on n'enregistre pas
        verifyImg.onerror = () => {
          logErrorToBackend(
            new Error(`Resource loading error: ${verifySrc}`),
            { type: 'resource', tagName, src: verifySrc, rel: event.target.rel },
            window.location.href
          );
        };
        verifyImg.src = verifySrc;
        return;
      }

      // Ignorer les erreurs de scripts tiers non critiques (bloqueurs de pub, pare-feux)
      const THIRD_PARTY_SCRIPT_PATTERNS = [
        'googletagmanager.com',
        'google-analytics.com',
        'analytics.google.com',
        'doubleclick.net',
        'facebook.net',
        'connect.facebook.net',
        'hotjar.com',
        'clarity.ms',
        'js.stripe.com/clover/', // Script Stripe/Clover injecté par extensions navigateur (non critique)
      ];
      if (tagName === 'SCRIPT' && src && THIRD_PARTY_SCRIPT_PATTERNS.some(p => src.includes(p))) {
        return;
      }

      // Logger les autres erreurs de ressources (CSS, fonts, scripts, etc.)
      logErrorToBackend(
        new Error(`Resource loading error: ${src}`),
        { 
          type: 'resource',
          tagName: tagName,
          src: src,
          rel: event.target.rel
        },
        window.location.href
      );
    } else {
      // Les scripts d'extensions s'exécutent dans la page : filtrer sur le fichier
      // d'origine et la stack, pas seulement sur le message.
      const runtimeStack = `${event.filename || ''}\n${event.error?.stack || ''}`;
      if (isBrowserExtensionError(event.message, runtimeStack)) {
        return;
      }

      logErrorToBackend(
        new Error(event.message),
        { 
          type: 'runtime',
          filename: event.filename, 
          lineno: event.lineno, 
          colno: event.colno,
          error: event.error?.toString()
        },
        window.location.href
      );
    }
  }, true); // useCapture = true pour capturer les erreurs de ressources

  // 2. Capturer les promesses rejetées non gérées
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;

    // Ignorer les erreurs d'extensions navigateur
    const reasonMsg = reason instanceof Error ? reason.message : String(reason?.reason ?? reason ?? '');
    const reasonStack = reason?.stack || '';
    if (isBrowserExtensionError(reasonMsg, reasonStack)) {
      return;
    }
    let errorMessage = 'Unhandled Promise Rejection';
    let errorDetails = {};

    if (reason instanceof Error) {
      errorMessage = reason.message;
      errorDetails = {
        type: 'promise',
        stack: reason.stack,
        name: reason.name
      };
    } else if (typeof reason === 'object') {
      errorMessage = JSON.stringify(reason);
      errorDetails = {
        type: 'promise',
        reason: reason
      };
    } else {
      errorMessage = String(reason);
      errorDetails = {
        type: 'promise',
        reason: reason
      };
    }

    logErrorToBackend(
      new Error(errorMessage),
      errorDetails,
      window.location.href
    );
  });

  // 3. Capturer les erreurs de console.error (optionnel mais utile)
  console.error = function(...args) {
    // Appeler l'original
    originalConsoleError.apply(console, args);
    
    // Logger vers le backend seulement en production
    if (process.env.NODE_ENV === 'production') {
      const errorMessage = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      // Filtrer les erreurs GA4/GTM/Analytics qui sont normales (bloqueurs de pub)
      const isAnalyticsError = errorMessage.includes('GA4') || 
                               errorMessage.includes('GTM') ||
                               errorMessage.includes('Google Analytics') ||
                               errorMessage.includes('googletagmanager') ||
                               errorMessage.includes('google-analytics');

      // Filtrer les erreurs de refresh token avec codes 4xx (session expirée = comportement normal)
      // 401 = token invalide, 403 = accès refusé, 404 = utilisateur supprimé
      const isExpiredSessionError = errorMessage.includes('Token refresh failed') &&
                                    (errorMessage.includes('"status":401') ||
                                     errorMessage.includes('"status":403') ||
                                     errorMessage.includes('"status":404') ||
                                     errorMessage.includes('status code 401') ||
                                     errorMessage.includes('status code 403') ||
                                     errorMessage.includes('status code 404'));

      // Filtrer les erreurs fetch sans aucun détail (objet {} vide) : signe quasi
      // certain d'une extension navigateur (bloqueur de pub/tracker) qui intercepte
      // la requête plutôt qu'un vrai échec applicatif.
      const isEmptyFetchError = /Fetch request failed:\s*\{\}$/.test(errorMessage);

      // Extensions navigateur : détecter via le message ET la stack des Error passées
      const argsStack = args
        .map(arg => (arg instanceof Error ? arg.stack || '' : ''))
        .join('\n');
      const isExtensionError = isBrowserExtensionError(errorMessage, argsStack);

      if (!isAnalyticsError && !isExpiredSessionError && !isEmptyFetchError && !isExtensionError) {
        logErrorToBackend(
          new Error(`Console Error: ${errorMessage}`),
          { type: 'console', args: args },
          window.location.href
        );
      }
    }
  };

  // 4. Retry périodique pour les erreurs en file d'attente
  setInterval(() => {
    if (errorQueue.length > 0) {
      processErrorQueue();
    }
  }, 30000); // Retry toutes les 30 secondes

  // 5. Envoyer les erreurs en attente avant de quitter la page
  window.addEventListener('beforeunload', () => {
    if (errorQueue.length > 0) {
      // Utiliser sendBeacon pour envoyer de manière asynchrone même si la page se ferme
      errorQueue.forEach(errorData => {
        try {
          navigator.sendBeacon(API_ENDPOINT, JSON.stringify(errorData));
        } catch (e) {
          // Ignore
        }
      });
    }
  });
};
